/**
 * The occupancy engine — who holds the contested column, and why.
 *
 * ## Why this exists at all
 *
 * A conventional responsive system asks one question: how wide is the viewport.
 * These surfaces ask two — how wide is the viewport, AND what else is placed
 * right now. Measured across the seven hub anchors, each carries 104–134
 * `:has()` rules of which 12–14 change layout. That is not decoration; it is a
 * state machine, and it is the reason a widget's internal grid can answer to
 * its neighbour rather than only to the screen.
 *
 * The clearest instance, from `rttr-drop-hub.html:618-621` — the earn grid is
 * three cards across, UNLESS a quest column is placed beside it, then two,
 * UNLESS the viewport is wide enough to afford three anyway:
 *
 *     .ptg                                { grid-template-columns: 1fr 1fr; }
 *     @media(min-width:1024px) .ptg       { grid-template-columns: repeat(3,1fr); }
 *     .app:has(#e-jq:checked) .ptg        { grid-template-columns: repeat(2,minmax(0,1fr)); }
 *     @media(min-width:1500px)
 *       .app:has(#e-jq:checked) .ptg      { grid-template-columns: repeat(3,minmax(0,1fr)); }
 *
 * So occupancy has to be a value the structure publishes and widgets can read.
 * Build responsiveness on viewport alone and this page cannot be expressed.
 *
 * ## The three rules, and each one is a bug that was found the hard way
 *
 * 1. **AUTO is the ABSENCE OF A VALID PIN — never a value.** If AUTO were a
 *    third state you could pin, "unpin" would need its own affordance and the
 *    resolver would have to rank three things instead of two.
 *
 * 2. **A pin is only a pin while its occupant exists.** In the anchors the tabs
 *    are radios, so pinning Standings and then ending that leaderboard leaves
 *    the radio checked and the column stranded empty. Every pin is therefore
 *    validated against live occupancy on every resolve — a stale pin degrades
 *    to AUTO rather than to nothing.
 *
 * 3. **A control appears only where there is a real choice.** One candidate
 *    means one label, and a control with nothing to choose reads as a demo
 *    switch. `showControl` is false at fewer than two candidates.
 */

import {SurfaceSlot, columnCandidates} from "./slots";

/** What the stage is doing. The column's width and the candidate set follow. */
export enum StageState {
    /** Nothing playing. The stage shows its idle occupant. */
    IDLE = "IDLE",
    /** Something is playing at standard size. */
    WATCH = "WATCH",
    /**
     * Expanded. The stage grows and the column gives up 60px for it — it does
     * NOT swallow the column. A viewer who wants to talk is the viewer most
     * likely to leave for another app to do it, so the room stays on the page.
     */
    EXPANDED = "EXPANDED",
}

/** Column widths, measured off the anchors (`.side` 400px; 340px under `#s-big`). */
export const COLUMN_WIDTH_PX = 400;
export const COLUMN_WIDTH_EXPANDED_PX = 340;

export interface OccupancyInput {
    slots: readonly SurfaceSlot[];
    stage: StageState;
    /** The viewer's pin, if they have expressed one. May be stale — that is handled. */
    pinnedSlotId?: string | null;
}

export interface OccupancyState {
    /** The slot that actually holds the column, or null when nothing can. */
    columnOccupantId: string | null;
    /** Every slot eligible for the column right now. */
    candidateIds: string[];
    /** True when the occupant was chosen by the viewer rather than derived. */
    isPinned: boolean;
    /** Whether to render the occupant control. False below two candidates. */
    showControl: boolean;
    /** The column's current width in px, or null when it is not held. */
    columnWidthPx: number | null;
}

/**
 * Resolve who holds the column.
 *
 * Deliberately pure and total: same inputs, same answer, no throw. It is the
 * one piece of the structure layer that carries real logic, so it is the one
 * piece that can be unit-tested to exhaustion — which is the point.
 */
export function resolveOccupancy({slots, stage, pinnedSlotId}: OccupancyInput): OccupancyState {
    /*
     * Stage state now shapes CANDIDACY, not only width (defect 5): an
     * Expanded-only slot enters the candidate set exactly when the stage
     * expands, and rule 2 already handles the collapse — its pin degrades to
     * AUTO the moment it stops being a candidate.
     */
    const candidates = columnCandidates(slots, stage);
    const candidateIds = candidates.map(s => s.id);

    /*
     * Rule 2, and it is the whole reason this is a function rather than a
     * field: the pin is validated against LIVE candidates every time. A pin
     * naming a slot that has since ended is not a pin — it degrades to AUTO,
     * so the column falls back to something real instead of stranding empty.
     */
    const validPin = pinnedSlotId && candidateIds.includes(pinnedSlotId)
        ? pinnedSlotId
        : null;

    /* Rule 1: AUTO is what happens when there is no valid pin. It is not a value. */
    const columnOccupantId = validPin ?? candidateIds[0] ?? null;

    /*
     * A width only means anything when something is holding the column, so the
     * "is it held" question is answered before the "how wide" one rather than
     * nesting the two.
     */
    const heldWidth = stage === StageState.EXPANDED ? COLUMN_WIDTH_EXPANDED_PX : COLUMN_WIDTH_PX;

    return {
        columnOccupantId,
        candidateIds,
        isPinned: validPin !== null,
        /* Rule 3: one label is not a choice. */
        showControl: candidateIds.length > 1,
        columnWidthPx: columnOccupantId === null ? null : heldWidth,
    };
}

/**
 * The occupancy signal, published as data attributes on the structure root so
 * descendants can answer to their neighbours in plain CSS:
 *
 *     [data-occupancy~="column"] .earn-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
 *
 * A space-separated token list rather than one value per attribute, so `~=`
 * matches a single token and adding a signal never invalidates a selector that
 * did not ask about it.
 */
export function occupancySignal(state: OccupancyState, slots: readonly SurfaceSlot[]): string {
    const tokens: string[] = [];
    if (state.columnOccupantId) tokens.push("column");
    if (state.showControl) tokens.push("contested");
    if (slots.some(s => s.present !== false && s.footprint === "COLUMN_WIDGET")) tokens.push("column-widget");
    if (slots.some(s => s.present !== false && s.footprint === "RAIL")) tokens.push("rail");
    return tokens.join(" ");
}
